import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../redux/store';
import {
  dismissBankTransactionThunk,
  fetchBankTransactionsThunk,
  linkBankTransactionThunk,
  updateCashAppSettingsThunk,
} from '../../../../redux/services/cashApplicationService';
import type { BankTransactionRecord, RemittanceRecord } from '@dill/dill-shared';
import { depositUnaccountedAmount, isDepositOpenForLinking } from '@dill/dill-shared';
import { DillTable, DillButton, DillModal } from '../../../general/Dill';
import type { DillColumn, DillRowAction, DillSummaryCard } from '../../../general/Dill';
import {
  Bank,
  CheckCircle,
  Clock,
  LinkSimple,
  NotePencil,
  Plus,
  Prohibit,
  UserMinus,
  Warning,
  X,
} from 'phosphor-react';
import { openDillToast } from '../../../../utils/helpers';
import { formatCurrency, formatDate, isUsableIgnorePattern, matchesIgnorePattern } from '../utils';
import { AddRemittanceModal } from './AddRemittanceModal';

/**
 * Money we hold that no advice explains.
 *
 * The bank feed says a payment went through and nothing about how to apply it, so a credit
 * with no matching advice cannot be posted — but it must not be invisible either. Deposits sit
 * here until the advice arrives (ingestion re-checks this queue automatically), or until an
 * operator links one by hand.
 *
 * Manual linking is not a convenience: matching is exact-to-the-cent, so a deposit reduced by
 * a wire fee, a short-pay, or an advice dated outside the window can never pair automatically.
 */

const describeDeposit = (record: BankTransactionRecord): string =>
  record.merchantName || record.name || 'Bank deposit';

interface DepositRow {
  id: string;
  amount: number;
  date: string;
  description: string;
  payerOnDeposit: string;
  customer: string;
  bankStatus: BankTransactionRecord['bankStatus'];
  matchStatus: BankTransactionRecord['matchStatus'];
  record: BankTransactionRecord;
}

/**
 * The payments one document produced, offered to a deposit as a unit.
 *
 * An ungrouped record is a group of one, so the picker has a single rendering path rather than
 * two that can disagree about what is selectable.
 */
interface LinkCandidateGroup {
  key: string;
  batchId: string | null;
  fileName: string | null;
  records: RemittanceRecord[];
  total: number;
  /** How much of the deposit this group leaves unaccounted for. */
  delta: number;
  sameBuyer: boolean;
}

/**
 * Shown when a deposit names nobody — a cash deposit, a remote deposit capture, a generic wire.
 * Sorts to the end of an ascending sort, which is where the manual work is.
 */
const UNIDENTIFIED_CUSTOMER = 'Unidentified';
const NO_PAYER_NAMED = 'No payer named';

/** Which of Plaid's fields a name was read from, in words rather than the stored enum. */
const sourceLabel = (source: BankTransactionRecord['buyerMatchSource']): string => {
  switch (source) {
    case 'COUNTERPARTY':
      return "bank's counterparty";
    case 'PAYMENT_META':
      return 'ACH payer field';
    case 'MERCHANT_NAME':
      return 'merchant name';
    default:
      return 'bank description';
  }
};

export const UnmatchedDepositsQueue = () => {
  const dispatch = useAppDispatch();
  const { bankTransactions, awaitingSettlementItems, loadingList } = useAppSelector(
    (state) => state.cashApplication
  );
  const currentSupplier = useAppSelector((state) => state.auth.currentSupplier);
  const supplierId = currentSupplier?.id;
  const bankFeedLinked = currentSupplier?.cashAppBankFeed?.enabled === true;

  const [linkTarget, setLinkTarget] = useState<BankTransactionRecord | null>(null);
  // Which payments this deposit is about to be linked to. Several, because a bulk check deposit
  // confirms a whole file at once.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [recordTarget, setRecordTarget] = useState<BankTransactionRecord | null>(null);
  const [ignoreTarget, setIgnoreTarget] = useState<BankTransactionRecord | null>(null);
  // Editable, not taken verbatim from the row: the bank appends a date and trace number, so the
  // raw description would only ever match this one deposit.
  const [ignorePattern, setIgnorePattern] = useState('');
  // The list is managed from here as well as Settings — it is only ever wrong in the presence of
  // this queue.
  const [manageOpen, setManageOpen] = useState(false);
  const [managePattern, setManagePattern] = useState('');
  const [busy, setBusy] = useState(false);
  const savedSettings = useAppSelector((state) => state.cashApplication.settings);
  // No Save button here, so each edit is its own request and the store is the only draft.
  const ignoredPayers = savedSettings.ignoredDepositPayers ?? [];

  const loading = loadingList.includes('fetchBankTransactions');

  // Matched deposits belong with their remittance record, and dismissed ones are settled
  // business. Only the open ones are a worklist.
  const rows = useMemo<DepositRow[]>(
    () =>
      bankTransactions.filter(isDepositOpenForLinking).map((record) => ({
        id: record.id,
        amount: record.amount,
        date: record.date,
        description: describeDeposit(record),
        payerOnDeposit: record.payerNameOnDeposit ?? NO_PAYER_NAMED,
        customer: record.matchedBuyerName ?? UNIDENTIFIED_CUSTOMER,
        bankStatus: record.bankStatus,
        matchStatus: record.matchStatus,
        record,
      })),
    [bankTransactions]
  );

  const summaryCards = useMemo<DillSummaryCard[]>(() => {
    const cleared = rows.filter((row) => row.bankStatus === 'POSTED');
    const clearedTotal = cleared.reduce((sum, row) => sum + row.amount, 0);
    const uncleared = rows.filter((row) => row.bankStatus === 'PENDING');

    return [
      {
        label: 'Unexplained deposits',
        value: formatCurrency(clearedTotal),
        subLabel: `${cleared.length} settled deposit(s)`,
        icon: <Bank size={16} />,
        variant: cleared.length > 0 ? 'warning' : 'default',
      },
      {
        label: 'Not yet settled',
        value: formatCurrency(uncleared.reduce((sum, row) => sum + row.amount, 0)),
        subLabel: `${uncleared.length} pending at bank`,
        icon: <Clock size={16} />,
      },
    ];
  }, [rows]);

  /**
   * Candidates for a manual link, grouped by the document they came from.
   *
   * Grouping is the point of this list, not decoration. Check remittance is banked in bulk — one
   * file of checks arrives as a single credit for their sum — so the unit that explains a deposit
   * is often the whole file rather than any one payment in it. `batchId`, stamped at ingestion,
   * is what says which records arrived together.
   *
   * Ordering keeps the previous rule: the deposit's own customer first, then closeness. When we
   * know who paid but the amount is off — a wire fee, a short pay — their advice is far likelier
   * than a stranger's exact match. A bulk deposit names nobody, so for those it degenerates to
   * closeness and the file that adds up sorts first, which is exactly what is wanted.
   */
  const linkCandidates = useMemo<LinkCandidateGroup[]>(() => {
    if (!linkTarget) {
      return [];
    }

    const groups = new Map<string, RemittanceRecord[]>();
    for (const record of awaitingSettlementItems) {
      // Ungrouped records are keyed by their own id, so they read as a group of one and the
      // rendering path stays single.
      const key = record.batchId ?? `record:${record.id}`;
      const existing = groups.get(key);
      if (existing) {
        existing.push(record);
      } else {
        groups.set(key, [record]);
      }
    }

    return [...groups.entries()]
      .map(([key, records]) => {
        const total = Math.round(records.reduce((sum, r) => sum + r.amount, 0) * 100) / 100;
        return {
          key,
          batchId: records[0].batchId ?? null,
          fileName: records[0].sourceFile?.name ?? null,
          records,
          total,
          // Against what the deposit still owes, so one part-linked earlier asks for the
          // remainder rather than its face value again.
          delta: Math.round((depositUnaccountedAmount(linkTarget) - total) * 100) / 100,
          // Only meaningful when the deposit was attributed at all; an unidentified deposit
          // leaves every candidate equally plausible and falls back to pure closeness.
          sameBuyer:
            !!linkTarget.matchedBuyerId &&
            records.every((r) => r.buyerId === linkTarget.matchedBuyerId),
        };
      })
      .sort((a, b) => {
        if (a.sameBuyer !== b.sameBuyer) {
          return a.sameBuyer ? -1 : 1;
        }
        return Math.abs(a.delta) - Math.abs(b.delta);
      });
  }, [linkTarget, awaitingSettlementItems]);

  const selectedRecords = useMemo(
    () => linkCandidates.flatMap((group) => group.records).filter((r) => selectedIds.has(r.id)),
    [linkCandidates, selectedIds]
  );

  const selectedTotal = useMemo(
    () => Math.round(selectedRecords.reduce((sum, r) => sum + r.amount, 0) * 100) / 100,
    [selectedRecords]
  );

  // How much of the deposit is still unexplained, and how much of it a previous visit already
  // accounted for — both shown, so a partial link is legible rather than looking like a shortfall.
  const availableToAccount = linkTarget ? depositUnaccountedAmount(linkTarget) : 0;
  const alreadyLinked = linkTarget
    ? Math.round((linkTarget.amount - availableToAccount) * 100) / 100
    : 0;
  const remainingToAccount = Math.round((availableToAccount - selectedTotal) * 100) / 100;

  const openLinkModal = useCallback((deposit: BankTransactionRecord) => {
    setLinkTarget(deposit);
    setSelectedIds(new Set());
  }, []);

  /**
   * Ticks the best-fitting file when the picker opens.
   *
   * The common case is a whole document explained by one deposit, and asking an operator to tick
   * five checks they never chose to split is busywork. Only a multi-record group is pre-selected,
   * and only when it is the closest fit of anything on offer: ticking a single payment on the
   * operator's behalf would put an accidental link one click away, which is the mistake this
   * screen exists to prevent.
   *
   * Keyed on the deposit rather than the candidate list, so re-ranking after a background refetch
   * cannot silently revise what is ticked underneath the operator.
   */
  useEffect(() => {
    if (!linkTarget) {
      return;
    }
    const [best] = linkCandidates;
    if (best && best.records.length > 1) {
      setSelectedIds(new Set(best.records.map((r) => r.id)));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkTarget?.id]);

  const toggleRecord = useCallback((recordId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(recordId)) {
        next.delete(recordId);
      } else {
        next.add(recordId);
      }
      return next;
    });
  }, []);

  const toggleGroup = useCallback((records: RemittanceRecord[]) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      const allSelected = records.every((r) => next.has(r.id));
      records.forEach((r) => (allSelected ? next.delete(r.id) : next.add(r.id)));
      return next;
    });
  }, []);

  const handleLink = async () => {
    if (!supplierId || !linkTarget || selectedRecords.length === 0) {
      return;
    }
    setBusy(true);
    try {
      const result = await dispatch(
        linkBankTransactionThunk({
          supplierId,
          bankTransactionId: linkTarget.id,
          remittanceIds: selectedRecords.map((r) => r.id),
        })
      );

      if (result.meta.requestStatus === 'fulfilled') {
        const { settled } = result.payload as { settled: number };
        openDillToast({
          message:
            settled > 1
              ? `Deposit linked to ${settled} payments. Approve them in 1-Click Review to post.`
              : 'Deposit linked. Approve the payment in 1-Click Review to post it.',
          type: 'SUCCESS',
        });
        setLinkTarget(null);
        setSelectedIds(new Set());
      } else {
        openDillToast({
          message: (result.payload as string) ?? 'Failed to link the deposit.',
          type: 'ERROR',
        });
      }
    } finally {
      setBusy(false);
    }
  };

  const handleDismiss = useCallback(
    async (record: BankTransactionRecord) => {
      if (!supplierId) {
        return;
      }
      const result = await dispatch(
        dismissBankTransactionThunk({ supplierId, bankTransactionId: record.id })
      );

      if (result.meta.requestStatus === 'fulfilled') {
        openDillToast({
          message: 'Deposit dismissed. It will not appear in this queue again.',
          type: 'SUCCESS',
        });
      } else {
        openDillToast({
          message: (result.payload as string) ?? 'Failed to dismiss the deposit.',
          type: 'ERROR',
        });
      }
    },
    [dispatch, supplierId]
  );

  // What else this pattern would dismiss, shown before saving: it is a standing rule, and too
  // broad a one loses real payments quietly.
  const ignorePreview = useMemo(() => {
    const pattern = ignorePattern.trim();
    if (!ignoreTarget || !pattern) {
      return [];
    }
    return bankTransactions.filter(
      (record) =>
        record.id !== ignoreTarget.id &&
        (record.matchStatus === 'UNMATCHED' || record.matchStatus === 'AMBIGUOUS') &&
        matchesIgnorePattern(record, pattern)
    );
  }, [bankTransactions, ignorePattern, ignoreTarget]);

  // As ignorePreview, but with no row to exclude — this pattern is not tied to one deposit.
  const managePreview = useMemo(() => {
    const pattern = managePattern.trim();
    if (!manageOpen || !pattern) {
      return [];
    }
    return bankTransactions.filter(
      (record) =>
        (record.matchStatus === 'UNMATCHED' || record.matchStatus === 'AMBIGUOUS') &&
        matchesIgnorePattern(record, pattern)
    );
  }, [bankTransactions, managePattern, manageOpen]);

  const openIgnoreModal = useCallback((record: BankTransactionRecord) => {
    setIgnorePattern(record.payerNameOnDeposit ?? record.merchantName ?? record.name ?? '');
    setIgnoreTarget(record);
  }, []);

  /**
   * Writes a new ignore list and reports what the backend's sweep did with it. The settings save
   * runs the same sweep the bank sync does, so one call both stores the rule and clears the queue.
   */
  const commitIgnoredPayers = useCallback(
    async ({
      next,
      successMessage,
      failureMessage,
    }: {
      next: string[];
      successMessage: (dismissed: number) => string;
      failureMessage: string;
    }): Promise<boolean> => {
      if (!supplierId) {
        return false;
      }

      setBusy(true);
      try {
        const result = await dispatch(
          updateCashAppSettingsThunk({ supplierId, ignoredDepositPayers: next })
        );

        if (result.meta.requestStatus !== 'fulfilled') {
          openDillToast({
            message: (result.payload as string) ?? failureMessage,
            type: 'ERROR',
          });
          return false;
        }

        const dismissed =
          (result.payload as { ignoredDismissed?: number } | undefined)?.ignoredDismissed ?? 0;
        openDillToast({ message: successMessage(dismissed), type: 'SUCCESS' });
        // The store still holds the swept deposits as unmatched until this lands.
        await dispatch(fetchBankTransactionsThunk({ supplierId }));
        return true;
      } finally {
        setBusy(false);
      }
    },
    [dispatch, supplierId]
  );

  const handleIgnorePayer = async () => {
    const pattern = ignorePattern.trim();
    if (!isUsableIgnorePattern(pattern)) {
      return;
    }

    // A duplicate is a no-op on the list, but the save still re-runs the sweep.
    const alreadyListed = ignoredPayers.some(
      (existing) => existing.toLowerCase() === pattern.toLowerCase()
    );

    const ok = await commitIgnoredPayers({
      next: alreadyListed ? ignoredPayers : [...ignoredPayers, pattern],
      successMessage: (dismissed) =>
        `"${pattern}" is now ignored. ${dismissed} deposit(s) removed from this queue.`,
      failureMessage: 'Failed to add the payer to the ignore list.',
    });

    if (ok) {
      setIgnoreTarget(null);
    }
  };

  const handleAddFromManage = async () => {
    const pattern = managePattern.trim();
    if (!isUsableIgnorePattern(pattern)) {
      return;
    }

    // As above: a duplicate is a no-op on the list, but the save still re-runs the sweep.
    const alreadyListed = ignoredPayers.some(
      (existing) => existing.toLowerCase() === pattern.toLowerCase()
    );

    const ok = await commitIgnoredPayers({
      next: alreadyListed ? ignoredPayers : [...ignoredPayers, pattern],
      successMessage: (dismissed) =>
        alreadyListed
          ? `"${pattern}" was already ignored. ${dismissed} deposit(s) removed from this queue.`
          : `"${pattern}" is now ignored. ${dismissed} deposit(s) removed from this queue.`,
      failureMessage: 'Failed to add the payer to the ignore list.',
    });

    if (ok) {
      setManagePattern('');
    }
  };

  // Changes what happens from now on only — the sweep never un-dismisses.
  const handleRemoveIgnoredPayer = async (pattern: string) => {
    await commitIgnoredPayers({
      next: ignoredPayers.filter((entry) => entry !== pattern),
      successMessage: () => `"${pattern}" is no longer ignored. Future deposits will appear here.`,
      failureMessage: 'Failed to update the ignore list.',
    });
  };

  const columns = useMemo<DillColumn<DepositRow>[]>(
    () => [
      {
        key: 'amount',
        label: 'Amount',
        sticky: true,
        numeric: true,
        width: 130,
        sortable: true,
        filterable: true,
        filterType: 'range',
        render: (value) => <span className="font-semibold">{formatCurrency(value as number)}</span>,
      },
      {
        key: 'date',
        label: 'Deposit date',
        width: 130,
        sortable: true,
        defaultSortDir: 'desc',
        filterable: true,
        filterType: 'dateRange',
        render: (value) => formatDate(value as string),
      },
      {
        key: 'description',
        label: 'Bank description',
        width: 280,
        sortable: true,
        getSearchValue: (row) => row.description,
        // Bank descriptions run long ("PREAUTHORIZED ACH CREDIT MERCH BNKCD DEPOSIT 240902 ...").
        // Two lines of the 48px row rather than one ellipsised line; min-w-0 is what lets the
        // clamp work, since a flex item will not shrink below its content without it.
        render: (value, row) => (
          <span className="line-clamp-2 break-words min-w-0 leading-4" title={row.description}>
            {row.description}
          </span>
        ),
      },
      {
        key: 'payerOnDeposit',
        label: 'Payer on deposit',
        width: 200,
        sortable: true,
        filterable: true,
        getSearchValue: (row) => row.payerOnDeposit,
        render: (value, row) =>
          row.record.payerNameOnDeposit ? (
            <span
              className="line-clamp-2 break-words min-w-0 leading-4"
              // Which field this was read from, so a wrong reading is traceable.
              title={`${row.record.payerNameOnDeposit} — from the ${sourceLabel(
                row.record.buyerMatchSource
              )}`}>
              {row.record.payerNameOnDeposit} {row.record.payerNameOnDeposit}{' '}
              {row.record.payerNameOnDeposit} {row.record.payerNameOnDeposit}
            </span>
          ) : (
            <span className="text-xs text-gray-400 italic">{NO_PAYER_NAMED}</span>
          ),
      },
      {
        key: 'customer',
        label: 'Dill customer',
        width: 200,
        sortable: true,
        filterable: true,
        getSearchValue: (row) => row.customer,
        render: (value, row) => {
          if (row.record.matchedBuyerName) {
            return (
              <span
                className="line-clamp-2 break-words min-w-0 leading-4"
                title={`${row.record.matchedBuyerName} — matched at ${Math.round(
                  (row.record.buyerMatchConfidence ?? 0) * 100
                )}% from the ${sourceLabel(row.record.buyerMatchSource)}`}>
                {row.record.matchedBuyerName}
              </span>
            );
          }
          /**
           * The distinction the two columns exist for.
           *
           * A named payer with no customer behind it is a gap in the buyer list — someone
           * should probably be created or an alias fixed. A deposit naming nobody was never
           * matchable and only ever wants a manual link. Both used to read as one empty cell.
           */
          return row.record.payerNameOnDeposit ? (
            <span className="text-xs text-amber-600">No matching customer</span>
          ) : (
            <span className="text-xs text-gray-400 italic">{UNIDENTIFIED_CUSTOMER}</span>
          );
        },
      },
      {
        key: 'bankStatus',
        label: 'Bank status',
        width: 150,
        filterable: true,
        render: (value) =>
          value === 'PENDING' ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset bg-amber-50 text-amber-700 ring-amber-600/20">
              <Clock size={12} weight="bold" />
              Pending at bank
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset bg-emerald-50 text-emerald-700 ring-emerald-600/20">
              Settled
            </span>
          ),
      },
      {
        key: 'matchStatus',
        label: 'Status',
        width: 190,
        filterable: true,
        /**
         * The deposit-side mirror of the record's settlement status. A deposit is waiting for
         * its advice in exactly the way a record is waiting for its deposit, so the two tables
         * describe the same gap from opposite ends and should read that way.
         *
         * `AMBIGUOUS` stays distinct rather than collapsing into "awaiting remittance": several
         * advices fit and the matcher refused to guess, which needs a person to choose, not more
         * waiting.
         */
        render: (value, row) => {
          if (value === 'MATCHED') {
            return (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset whitespace-nowrap bg-emerald-50 text-emerald-700 ring-emerald-600/20">
                <CheckCircle size={12} weight="fill" />
                Matched
              </span>
            );
          }
          {
            /*
              Names the shortfall rather than just the state. "Partly matched" alone would leave
              the operator opening the deposit to find out how much is missing, which is the one
              thing they need in order to decide whether to finish it now.
            */
          }
          if (value === 'PARTIALLY_MATCHED') {
            return (
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset whitespace-nowrap bg-blue-50 text-blue-700 ring-blue-600/20"
                title={`${formatCurrency(row.record.matchedAmount ?? 0)} of ${formatCurrency(
                  row.amount
                )} is linked to ${row.record.matchedRemittanceIds?.length ?? 0} payment(s)`}>
                {formatCurrency(depositUnaccountedAmount(row.record))} left
              </span>
            );
          }
          if (value === 'AMBIGUOUS') {
            return (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset whitespace-nowrap bg-orange-50 text-orange-700 ring-orange-600/20">
                Several candidates
              </span>
            );
          }
          return (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ring-1 ring-inset whitespace-nowrap bg-amber-50 text-amber-700 ring-amber-600/20">
              <Clock size={12} weight="bold" />
              Awaiting remittance
            </span>
          );
        },
      },
    ],
    []
  );

  const rowActions = useMemo<DillRowAction<DepositRow>[]>(
    () => [
      {
        key: 'link',
        label: 'Link',
        icon: <LinkSimple size={14} />,
        onClick: (row) => {
          // Uncleared money cannot settle anything, so linking it would be a lie.
          if (row.bankStatus !== 'POSTED') {
            openDillToast({
              message: 'This deposit has not cleared the bank yet, so it cannot be linked.',
              type: 'ERROR',
            });
            return;
          }
          openLinkModal(row.record);
        },
      },
      {
        key: 'record',
        label: 'Record payment',
        icon: <NotePencil size={14} />,
        onClick: (row) => {
          // Same rule as linking: money that has not cleared cannot settle anything, and this
          // action settles on save.
          if (row.bankStatus !== 'POSTED') {
            openDillToast({
              message: 'This deposit has not cleared the bank yet, so it cannot be linked.',
              type: 'ERROR',
            });
            return;
          }
          setRecordTarget(row.record);
        },
      },
      {
        key: 'dismiss',
        label: 'Dismiss',
        icon: <Prohibit size={14} />,
        variant: 'danger',
        onClick: (row) => handleDismiss(row.record),
      },
      {
        // Dismiss is for this deposit; this is for every deposit like it.
        key: 'ignore',
        label: 'Always ignore',
        icon: <UserMinus size={14} />,
        variant: 'danger',
        onClick: (row) => openIgnoreModal(row.record),
      },
    ],
    [handleDismiss, openIgnoreModal, openLinkModal]
  );

  return (
    <div className="flex flex-col w-full h-full min-h-0">
      <div className="flex items-start justify-between gap-4 px-1 py-3">
        <p className="text-sm text-gray-500">
          {bankFeedLinked
            ? 'Deposits in your bank feed with no matching remittance advice. Dill re-checks these whenever new advice arrives, so most clear themselves'
            : 'Deposits arrive here from your bank feed. No bank is connected yet, so there is nothing to confirm your remittance advice against.'}
        </p>
        {/* Always shown: an empty list is itself the answer to "why is that deposit not here". */}
        <DillButton
          onClick={() => setManageOpen(true)}
          label={
            ignoredPayers.length > 0 ? `Ignored payers (${ignoredPayers.length})` : 'Ignored payers'
          }
          variant="ghost"
          icon={<Prohibit size={14} />}
          height={32}
        />
      </div>

      <DillTable<DepositRow>
        className="flex-1 min-h-0"
        columns={columns}
        data={rows}
        rowKey={(row) => row.id}
        search={true}
        searchPlaceholder="Search bank description..."
        striped={true}
        hoverVariant="default"
        emptyMessage={
          bankFeedLinked
            ? 'Every deposit in your feed is accounted for.'
            : 'Connect a bank in Data Integrations to confirm payments against real deposits.'
        }
        summaryCards={summaryCards}
        defaultSort={{ key: 'date', dir: 'desc' }}
        loading={loading}
        rowActions={rowActions}
        rowActionsMode="always"
        stickyRowActions={true}
        countLabel="deposits"
      />

      <DillModal
        open={!!linkTarget}
        onClose={() => setLinkTarget(null)}
        title="Link deposit to a remittance"
        subtitle={
          linkTarget
            ? `${formatCurrency(linkTarget.amount)} on ${formatDate(
                linkTarget.date
              )} — ${describeDeposit(linkTarget)}${
                // Named up front, so the smaller figure in the running total below is explained
                // before it is seen rather than after.
                alreadyLinked > 0
                  ? ` · ${formatCurrency(alreadyLinked)} already linked, ${formatCurrency(
                      availableToAccount
                    )} left to explain`
                  : ''
              }`
            : undefined
        }
        maxWidth="640px"
        footer={
          <>
            <DillButton onClick={() => setLinkTarget(null)} label="Cancel" variant="ghost" />
            <DillButton
              onClick={handleLink}
              label={
                busy
                  ? 'Linking...'
                  : selectedRecords.length > 1
                  ? `Link ${selectedRecords.length} payments`
                  : 'Link payment'
              }
              variant="primary"
              isLoading={busy}
              isDisabled={selectedRecords.length === 0}
            />
          </>
        }>
        {linkCandidates.length === 0 ? (
          <p className="text-sm text-gray-500">
            There are no unsettled remittance records to link this deposit to. Forward the
            remittance advice and it will match automatically.
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            <p className="text-xs text-gray-500">
              {linkTarget?.matchedBuyerName
                ? `${linkTarget.matchedBuyerName} first, then closest amounts.`
                : 'Closest amounts first.'}{' '}
              Pick every payment this deposit covers — a file of checks is banked as one credit, so
              it is often all of them. Linking confirms the money arrived; it does not skip the
              allocation review.
            </p>

            {/*
              The running total, pinned above the list.

              A bulk deposit is only explained when its parts add up, and that is a sum an operator
              would otherwise be doing in their head across a scrolling list.
            */}
            <div
              className={`flex items-center justify-between gap-3 p-3 rounded-lg border ${
                selectedRecords.length > 0 && remainingToAccount === 0
                  ? 'bg-emerald-50 border-emerald-200'
                  : 'bg-gray-50 border-gray-200'
              }`}>
              <p className="text-sm text-gray-700">
                <span className="font-semibold text-gray-900">
                  {selectedRecords.length} selected
                </span>{' '}
                · {formatCurrency(selectedTotal)} of {formatCurrency(availableToAccount)}
              </p>
              <p
                className={`text-sm font-medium ${
                  remainingToAccount === 0
                    ? 'text-emerald-700'
                    : remainingToAccount < 0
                    ? 'text-red-600'
                    : 'text-amber-600'
                }`}>
                {remainingToAccount === 0
                  ? 'Fully accounted for'
                  : remainingToAccount > 0
                  ? `${formatCurrency(remainingToAccount)} unaccounted`
                  : `${formatCurrency(Math.abs(remainingToAccount))} over`}
              </p>
            </div>

            {linkCandidates.map((group) => {
              const isBatch = group.records.length > 1;
              const allSelected = group.records.every((r) => selectedIds.has(r.id));

              return (
                <div
                  key={group.key}
                  className={`rounded-lg border ${
                    group.sameBuyer ? 'bg-emerald-50 border-emerald-200' : 'border-gray-200'
                  }`}>
                  {/*
                    Only a real group gets a heading. A lone record with a heading saying "1
                    payment" would be noise on the common case.
                  */}
                  {isBatch && (
                    <button
                      type="button"
                      onClick={() => toggleGroup(group.records)}
                      className="flex items-center justify-between w-full gap-3 px-3 pt-3 pb-2 text-left">
                      <div className="flex items-center min-w-0 gap-2">
                        <input
                          type="checkbox"
                          checked={allSelected}
                          readOnly
                          className="w-4 h-4 rounded pointer-events-none accent-blue-600"
                        />
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {group.fileName ?? 'One remittance file'}
                        </p>
                      </div>
                      <p className="text-xs text-gray-500 whitespace-nowrap">
                        {group.records.length} payments · {formatCurrency(group.total)}
                        {group.delta !== 0 && (
                          <span className="text-amber-600">
                            {' '}
                            · off by {formatCurrency(Math.abs(group.delta))}
                          </span>
                        )}
                      </p>
                    </button>
                  )}

                  <div className={isBatch ? 'flex flex-col px-3 pb-2' : 'flex flex-col'}>
                    {group.records.map((record) => (
                      <button
                        type="button"
                        key={record.id}
                        onClick={() => toggleRecord(record.id)}
                        className={`flex items-center justify-between w-full gap-3 text-left ${
                          isBatch ? 'py-1.5 pl-6' : 'p-3'
                        }`}>
                        <div className="flex items-center min-w-0 gap-2">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(record.id)}
                            readOnly
                            className="w-4 h-4 rounded pointer-events-none accent-blue-600"
                          />
                          <p className="text-sm font-medium text-gray-900 truncate">
                            {record.payerName ?? 'Unknown payer'}
                            {record.checkNumber && (
                              <span className="ml-2 text-xs font-normal text-gray-500">
                                check {record.checkNumber}
                              </span>
                            )}
                            {!isBatch && group.sameBuyer && (
                              <span className="ml-2 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-emerald-100 text-emerald-700">
                                Same customer
                              </span>
                            )}
                          </p>
                        </div>
                        <p className="text-xs text-gray-500 whitespace-nowrap">
                          {formatCurrency(record.amount)}
                          {record.adviceDate ? ` · ${formatDate(record.adviceDate)}` : ''}
                          {!isBatch && group.delta !== 0 && (
                            <span className="text-amber-600">
                              {' '}
                              · off by {formatCurrency(Math.abs(group.delta))}
                            </span>
                          )}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DillModal>

      <DillModal
        open={!!ignoreTarget}
        onClose={() => setIgnoreTarget(null)}
        title="Always ignore this payer"
        subtitle={
          ignoreTarget
            ? `${formatCurrency(ignoreTarget.amount)} on ${formatDate(
                ignoreTarget.date
              )} — ${describeDeposit(ignoreTarget)}`
            : undefined
        }
        maxWidth="560px"
        footer={
          <div className="flex items-center gap-2">
            <DillButton onClick={() => setIgnoreTarget(null)} label="Cancel" variant="ghost" />
            <DillButton
              onClick={handleIgnorePayer}
              label={busy ? 'Saving...' : 'Ignore this payer'}
              variant="primary"
              isLoading={busy}
              isDisabled={busy || !isUsableIgnorePattern(ignorePattern)}
            />
          </div>
        }>
        <div className="flex flex-col gap-3">
          <p className="text-sm text-gray-600">
            Deposits whose bank description contains this text will be kept out of this queue, now
            and in future. Trim it back to the part that repeats — the bank adds a date and a trace
            number to every deposit, so the full description would only ever match this one.
          </p>

          <div>
            <label className="text-xs font-medium text-gray-500">Ignore deposits containing</label>
            <input
              type="text"
              value={ignorePattern}
              onChange={(e) => setIgnorePattern(e.target.value)}
              className="mt-1 w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500"
            />
          </div>

          {ignorePattern.trim() && !isUsableIgnorePattern(ignorePattern) && (
            <p className="text-xs text-amber-700">
              Too short to ignore safely — use at least three letters or digits.
            </p>
          )}

          {ignoreTarget?.name && (
            <p className="text-xs text-gray-400">
              Full bank description:{' '}
              <span className="font-mono text-gray-500">{ignoreTarget.name}</span>
            </p>
          )}

          {ignorePreview.length > 0 && (
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
              <div className="flex items-center gap-1.5 mb-1.5">
                <Warning size={14} weight="fill" className="text-amber-600" />
                <span className="text-xs font-semibold text-amber-800">
                  {ignorePreview.length} other deposit(s) in this queue also match
                </span>
              </div>
              <p className="text-xs text-amber-700 mb-2">
                They will be dismissed too. Check none of them is a real customer payment.
              </p>
              <div className="flex flex-col gap-1 max-h-40 overflow-auto">
                {ignorePreview.map((record) => (
                  <div
                    key={record.id}
                    className="flex items-center justify-between gap-3 text-xs text-amber-900">
                    <span className="truncate min-w-0">
                      {formatDate(record.date)} — {describeDeposit(record)}
                      {record.matchedBuyerName && (
                        <span className="ml-1 font-semibold">({record.matchedBuyerName})</span>
                      )}
                    </span>
                    <span className="font-semibold tabular-nums shrink-0">
                      {formatCurrency(record.amount)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <p className="text-xs text-gray-400">
            You can remove it later under &ldquo;Ignored payers&rdquo; above this table. Deposits
            already dismissed stay dismissed.
          </p>
        </div>
      </DillModal>

      <DillModal
        open={manageOpen}
        onClose={() => setManageOpen(false)}
        title="Ignored payers"
        subtitle="Deposits containing these are kept out of this queue"
        maxWidth="600px"
        footer={<DillButton onClick={() => setManageOpen(false)} label="Done" variant="ghost" />}>
        <div className="flex flex-col gap-4">
          <p className="text-sm text-gray-600">
            Matching ignores case and punctuation, and a part of the description is enough — you do
            not need the date and trace number the bank appends. Every change here takes effect
            straight away.
          </p>

          <div className="flex gap-2">
            <input
              type="text"
              value={managePattern}
              disabled={busy}
              placeholder="e.g. PREAUTHORIZED ACH CREDIT INTUIT"
              onChange={(e) => setManagePattern(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  void handleAddFromManage();
                }
              }}
              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500 disabled:bg-gray-50"
            />
            <DillButton
              onClick={handleAddFromManage}
              label="Add"
              variant="secondary"
              icon={<Plus size={14} weight="bold" />}
              isLoading={busy}
              isDisabled={busy || !isUsableIgnorePattern(managePattern)}
              height={38}
            />
          </div>

          {managePattern.trim() && !isUsableIgnorePattern(managePattern) && (
            <p className="-mt-2 text-xs text-amber-700">
              Too short to ignore safely — use at least three letters or digits.
            </p>
          )}

          {managePreview.length > 0 && (
            <div className="p-3 rounded-lg bg-amber-50 border border-amber-200">
              <div className="flex items-center gap-1.5 mb-1.5">
                <Warning size={14} weight="fill" className="text-amber-600" />
                <span className="text-xs font-semibold text-amber-800">
                  {managePreview.length} deposit(s) in this queue match
                </span>
              </div>
              <div className="flex flex-col gap-1 max-h-32 overflow-auto">
                {managePreview.map((record) => (
                  <div
                    key={record.id}
                    className="flex items-center justify-between gap-3 text-xs text-amber-900">
                    <span className="truncate min-w-0">
                      {formatDate(record.date)} — {describeDeposit(record)}
                      {record.matchedBuyerName && (
                        <span className="ml-1 font-semibold">({record.matchedBuyerName})</span>
                      )}
                    </span>
                    <span className="font-semibold tabular-nums shrink-0">
                      {formatCurrency(record.amount)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <span className="text-xs font-medium text-gray-500">Your ignore list</span>
            {ignoredPayers.length === 0 ? (
              <p className="mt-1 text-sm text-gray-400 italic">
                Nothing is being ignored — every deposit reaches this queue. Use &ldquo;Always
                ignore&rdquo; on a deposit, or add a description above.
              </p>
            ) : (
              <div className="mt-2 flex flex-col divide-y divide-gray-100 rounded-lg border border-gray-200">
                {ignoredPayers.map((pattern) => (
                  <div
                    key={pattern}
                    className="flex items-center justify-between gap-3 px-3 py-2 text-sm">
                    <span className="truncate min-w-0 font-medium text-gray-800">{pattern}</span>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => handleRemoveIgnoredPayer(pattern)}
                      className="shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-gray-500 hover:bg-rose-50 hover:text-rose-700 disabled:opacity-40">
                      <X size={11} weight="bold" />
                      Stop ignoring
                    </button>
                  </div>
                ))}
              </div>
            )}
            {ignoredPayers.length > 0 && (
              <p className="mt-2 text-xs text-gray-400">
                Removing one changes what happens from now on. Deposits it already dismissed stay
                dismissed.
              </p>
            )}
          </div>
        </div>
      </DillModal>

      <AddRemittanceModal
        open={!!recordTarget}
        onClose={() => setRecordTarget(null)}
        deposit={recordTarget}
      />
    </div>
  );
};
